import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink } from '@tanstack/react-router';
import type {
  PollerEmailDecisionType,
  PollerNonActionableReason,
  PollerSelfSuppressReason,
} from '@app/shared';
import {
  Button,
  Cluster,
  Combobox,
  FormField,
  Heading,
  Link,
  SelectMenu,
  Spinner,
  Stack,
  Surface,
  Text,
  TextInput,
  type SelectMenuOption,
} from '../ui';
import { PageHeader } from '../components/PageHeader';
import { PollerDecisionsTable } from '../components/PollerDecisionsTable';
import { PollerConnectionHealthPanel } from '../components/PollerConnectionHealthPanel';
import { api, ApiError } from '../lib/api';
import { DECISION_META, REASON_LABELS, SELF_SUPPRESS_REASON_LABELS } from '../lib/pollerActivity';

/**
 * Poller Activity — the operator troubleshooting lookup (#318). Search a single user's poller
 * activity by their email OR a connection id, filter by decision / reason / since, and read the
 * per-email decision log ("why didn't my agent reply to X"). A connection-health panel surfaces
 * the poll cursor + recent fetches so "we never fetched this mailbox" is visible.
 *
 * Operator-only. The auth `me` payload carries no operator flag, so — like the poller sections on
 * the Admin page — we PROBE the operator-gated poller read: a non-operator's probe 403s (401
 * anonymous) and we render a restricted notice with NO lookup surface. Operators get the tool.
 */

interface Applied {
  email: string;
  connectionId: string;
  decision: string;
  reason: string;
  since: string;
}

const EMPTY_APPLIED: Applied = { email: '', connectionId: '', decision: '', reason: '', since: '' };

const DECISION_OPTIONS: SelectMenuOption[] = [
  { value: '', label: 'All decisions' },
  ...(Object.keys(DECISION_META) as PollerEmailDecisionType[]).map((d) => ({
    value: d,
    label: DECISION_META[d].label,
  })),
];

const REASON_OPTIONS: SelectMenuOption[] = [
  { value: '', label: 'All reasons' },
  ...(Object.keys(REASON_LABELS) as PollerNonActionableReason[]).map((r) => ({
    value: r,
    label: REASON_LABELS[r],
  })),
  // #328: the suppressed_self sub-reasons, so an operator can filter self-sent vs archived mail.
  ...(Object.keys(SELF_SUPPRESS_REASON_LABELS) as PollerSelfSuppressReason[]).map((r) => ({
    value: r,
    label: SELF_SUPPRESS_REASON_LABELS[r],
  })),
];

export function PollerActivityPage() {
  // Operator gate: reuse the operator-gated recent-runs read as a cheap probe (no cross-tenant
  // decision PII pulled just to check access). A non-operator's read 403s → restricted notice.
  const gate = useQuery({
    queryKey: ['admin', 'poller-activity', 'gate'],
    queryFn: () => api.getPollerRuns(1),
    retry: false,
  });
  const gateStatus = gate.error instanceof ApiError ? gate.error.status : undefined;

  if (gate.isPending) {
    return (
      <Stack gap="6">
        <PageHeader title="Poller activity" subtitle="Per-user email-poller troubleshooting" />
        <Cluster gap="2" align="center">
          <Spinner size="sm" />
          <Text size="sm" tone="muted">
            Checking access…
          </Text>
        </Cluster>
      </Stack>
    );
  }

  if (gateStatus === 403 || gateStatus === 401) {
    return (
      <Stack gap="6">
        <PageHeader title="Poller activity" />
        <Surface bordered radius="lg" padding="5" data-testid="poller-activity-restricted">
          <Stack gap="2">
            <Heading level={2} size="sm">
              Restricted
            </Heading>
            <Text size="sm" tone="secondary">
              This troubleshooting tool is limited to platform operators.
            </Text>
          </Stack>
        </Surface>
      </Stack>
    );
  }

  return <PollerActivityTool />;
}

/** The operator-only lookup tool (rendered once the operator gate passes). */
function PollerActivityTool() {
  // `userInput` is the raw text in the typeahead; `email` is the COMMITTED selection that becomes
  // the filter identity (set only by picking a user from the dropdown). Typing clears the
  // selection so a stale user can't linger behind edited text.
  const [userInput, setUserInput] = useState('');
  const [email, setEmail] = useState('');
  const [connectionId, setConnectionId] = useState('');
  const [decision, setDecision] = useState('');
  const [reason, setReason] = useState('');
  const [since, setSince] = useState('');

  const [applied, setApplied] = useState<Applied>(EMPTY_APPLIED);
  const [healthConnectionId, setHealthConnectionId] = useState<string | null>(null);

  // Typeahead over platform users: debounce the input (≥2 chars) so we query on a pause, not
  // per keystroke. The dropdown shows email (with the name as a secondary line).
  const debouncedUser = useDebouncedValue(userInput, 250);
  const userQueryReady = debouncedUser.trim().length >= 2 && debouncedUser === userInput;
  const usersQuery = useQuery({
    queryKey: ['admin', 'users', 'search', debouncedUser],
    queryFn: () => api.searchAdminUsers(debouncedUser, 10),
    enabled: userQueryReady,
    retry: false,
  });
  const userOptions: SelectMenuOption[] = (usersQuery.data?.users ?? []).map((u) => ({
    value: u.email,
    label: u.email,
    hint: u.name || undefined,
    'data-testid': 'poller-activity-user-option',
  }));

  // The connection dropdown is scoped to the SELECTED user (their workspaces' live connections),
  // refetched whenever the selected email changes. Empty until a user is picked.
  const connectionsQuery = useQuery({
    queryKey: ['admin', 'connections', email],
    queryFn: () => api.listAdminConnections(email),
    enabled: email.trim().length > 0,
    retry: false,
  });
  const connectionOptions: SelectMenuOption[] = [
    { value: '', label: 'All connections' },
    ...(connectionsQuery.data?.connections ?? []).map((c) => ({
      value: c.id,
      label: `${c.provider} · ${c.externalAccount}`,
    })),
  ];

  // A lookup needs an identity to start from — a selected user (email) OR a connection id (never
  // an unbounded cross-tenant dump). The other filters narrow that set.
  const canSearch = email.trim().length > 0 || connectionId.trim().length > 0;
  const hasApplied = applied.email.length > 0 || applied.connectionId.length > 0;

  const selectUser = (selectedEmail: string) => {
    setUserInput(selectedEmail);
    setEmail(selectedEmail);
    // A new user invalidates any connection picked for the previous one.
    setConnectionId('');
    setHealthConnectionId(null);
  };

  const clearUser = () => {
    setUserInput('');
    setEmail('');
    setConnectionId('');
    setHealthConnectionId(null);
  };

  const selectConnection = (id: string) => {
    setConnectionId(id);
    // Picking a specific connection focuses the health panel on it immediately (as a connection
    // search does); "All connections" (empty) clears that focus.
    setHealthConnectionId(id.length > 0 ? id : null);
  };

  const query = useQuery({
    queryKey: ['admin', 'poller-decisions', applied],
    queryFn: () =>
      api.getPollerDecisions({
        email: applied.email || undefined,
        connectionId: applied.connectionId || undefined,
        decision: applied.decision || undefined,
        reason: applied.reason || undefined,
        since: applied.since || undefined,
      }),
    enabled: hasApplied,
    retry: false,
  });

  const onSearch = () => {
    if (!canSearch) return;
    const trimmedConn = connectionId.trim();
    setApplied({
      email: email.trim(),
      connectionId: trimmedConn,
      decision,
      reason,
      since: toIso(since),
    });
    // Searching by a connection id focuses the health panel on it immediately.
    setHealthConnectionId(trimmedConn.length > 0 ? trimmedConn : null);
  };

  const decisions = query.data?.decisions ?? [];

  return (
    <Stack gap="6">
      <Link
        as={RouterLink}
        muted
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        {...({ to: '/app/admin' } as any)}
        data-testid="poller-activity-back"
      >
        ← Back to Admin
      </Link>

      <PageHeader
        title="Poller activity"
        subtitle="Look up one user's email-poller activity to diagnose a missed or skipped email."
      />

      <Surface bordered radius="lg" padding="5">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSearch();
          }}
        >
          <Stack gap="4">
            <Cluster gap="4" align="end" wrap>
              <div style={{ flex: '1 1 18rem', minWidth: 0 }}>
                <FormField
                  label="User"
                  help="Search platform users by email or name, then pick one to resolve their workspaces and connected mailboxes."
                >
                  <Combobox
                    inputValue={userInput}
                    onInputChange={(v) => {
                      setUserInput(v);
                      // Editing the text drops any prior selection until a new one is picked.
                      if (email.length > 0) {
                        setEmail('');
                        setConnectionId('');
                        setHealthConnectionId(null);
                      }
                    }}
                    options={userOptions}
                    onSelect={(opt) => selectUser(opt.value)}
                    onClear={clearUser}
                    label="Search users by email or name"
                    placeholder="Search by email or name…"
                    loading={userQueryReady && usersQuery.isFetching}
                    emptyText="No matching users."
                    minChars={2}
                    data-testid="poller-activity-email"
                  />
                </FormField>
              </div>
              <div style={{ flex: '1 1 18rem', minWidth: 0 }}>
                <FormField
                  label="Connection"
                  help="Filter to one of the selected user's connected mailboxes."
                >
                  <SelectMenu
                    label="Filter by connection"
                    value={connectionId}
                    onChange={selectConnection}
                    options={connectionOptions}
                    placeholder="All connections"
                    // Enabled once a user is picked; while their connections load the dropdown
                    // still offers "All connections" and fills in when the fetch returns.
                    disabled={email.trim().length === 0}
                    minWidth="100%"
                    data-testid="poller-activity-connection"
                  />
                </FormField>
              </div>
            </Cluster>

            <Cluster gap="4" align="end" wrap>
              <Stack gap="2" style={{ minWidth: '12rem' }}>
                <Text size="sm" weight="medium">
                  Decision
                </Text>
                <SelectMenu
                  label="Filter by decision"
                  value={decision}
                  onChange={setDecision}
                  options={DECISION_OPTIONS}
                  minWidth="12rem"
                  data-testid="poller-activity-decision"
                />
              </Stack>
              <Stack gap="2" style={{ minWidth: '12rem' }}>
                <Text size="sm" weight="medium">
                  Reason
                </Text>
                <SelectMenu
                  label="Filter by non-actionable reason"
                  value={reason}
                  onChange={setReason}
                  options={REASON_OPTIONS}
                  minWidth="12rem"
                  data-testid="poller-activity-reason"
                />
              </Stack>
              <div style={{ flex: '0 0 14rem' }}>
                <FormField label="Since">
                  <TextInput
                    type="datetime-local"
                    value={since}
                    onChange={(e) => setSince(e.target.value)}
                    data-testid="poller-activity-since"
                  />
                </FormField>
              </div>
            </Cluster>

            <Cluster gap="3" align="center">
              <Button type="submit" disabled={!canSearch} data-testid="poller-activity-search">
                Search
              </Button>
              {!canSearch && (
                <Text size="sm" tone="muted">
                  Enter a user email or a connection id to search.
                </Text>
              )}
            </Cluster>
          </Stack>
        </form>
      </Surface>

      {hasApplied && (
        <Surface bordered radius="lg" padding="5">
          <Stack gap="4">
            <Cluster gap="3" align="center" justify="between" wrap>
              <Heading level={2} size="sm">
                Emails
              </Heading>
              {query.isFetching && <Spinner size="sm" />}
            </Cluster>

            {query.isError ? (
              <Text size="sm" tone="danger" data-testid="poller-activity-error">
                {query.error instanceof ApiError ? query.error.message : 'Lookup failed.'}
              </Text>
            ) : (
              <PollerDecisionsTable
                decisions={decisions}
                caption="Per-email poller decisions for the searched user or connection"
                emptyLabel="No poller activity found for these filters."
                onConnectionClick={(id) => setHealthConnectionId(id)}
              />
            )}
          </Stack>
        </Surface>
      )}

      {healthConnectionId && <PollerConnectionHealthPanel connectionId={healthConnectionId} />}
    </Stack>
  );
}

/** Debounce a rapidly-changing value so an effect/query fires on a pause, not per keystroke. */
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

/** Convert a `datetime-local` value to an ISO-8601 instant; '' when absent/invalid. */
function toIso(local: string): string {
  if (!local) return '';
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString();
}
